const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface ExpDatePickerOptions {
  onChange?: () => void;
  onInteract?: () => void;
}

export function normalizeDateInput(raw: string | undefined | null): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const mdy = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (mdy) {
    const y = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    return `${y}-${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}`;
  }
  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function parseIsoParts(value: string): { y: number; m: number; d: number } | null {
  const iso = normalizeDateInput(value);
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((part) => Number(part));
  if (!y || !m || !d) return null;
  return { y, m, d };
}

function isoFromParts(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

function todayIso(): string {
  const d = new Date();
  return isoFromParts(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function closeAllDatePickers(except?: HTMLElement): void {
  for (const popover of document.querySelectorAll<HTMLElement>(".sfe-date-picker-popover")) {
    if (except && popover === except) continue;
    popover.hidden = true;
    popover.classList.remove("sfe-date-picker-popover-above");
    popover.closest(".sfe-date-picker")?.classList.remove("sfe-date-picker-open");
  }
}

function nudgeActionChipForPopover(popover: HTMLElement): void {
  const margin = 12;
  const chip = document.getElementById("sfe-focus-action-chip");
  if (!(chip instanceof HTMLElement)) return;

  const rect = popover.getBoundingClientRect();
  let shift = 0;
  if (rect.bottom > window.innerHeight - margin) {
    shift = rect.bottom - (window.innerHeight - margin);
  } else if (rect.top < margin) {
    shift = rect.top - margin;
  }
  if (!shift) return;

  const currentTop = Number.parseFloat(chip.style.top);
  const baseTop = Number.isFinite(currentTop) ? currentTop : chip.getBoundingClientRect().top;
  chip.style.top = `${Math.max(margin, baseTop - shift)}px`;
}

function positionDatePickerPopover(root: HTMLElement, popover: HTMLElement, anchor: HTMLElement): void {
  const margin = 12;
  popover.classList.remove("sfe-date-picker-popover-above");

  const anchorRect = anchor.getBoundingClientRect();
  const popoverHeight = popover.offsetHeight || 280;
  const gap = 6;
  const spaceBelow = window.innerHeight - margin - anchorRect.bottom;
  const spaceAbove = anchorRect.top - margin;
  const needed = popoverHeight + gap;
  const preferAbove = anchorRect.top > window.innerHeight * 0.52;

  if (preferAbove || (spaceBelow < needed && spaceAbove >= spaceBelow)) {
    popover.classList.add("sfe-date-picker-popover-above");
  }

  requestAnimationFrame(() => {
    const rect = popover.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - margin || rect.top < margin) {
      nudgeActionChipForPopover(popover);
    }
  });
}

function bindDocumentClose(): void {
  if (document.documentElement.dataset.sfeDatePickerBound === "true") return;
  document.documentElement.dataset.sfeDatePickerBound = "true";
  document.addEventListener(
    "pointerdown",
    (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".sfe-date-picker")) return;
      closeAllDatePickers();
    },
    true,
  );
}

export function mountExpDatePicker(
  input: HTMLInputElement,
  raw?: string | null,
  options: ExpDatePickerOptions = {},
): () => void {
  bindDocumentClose();

  let viewYear: number;
  let viewMonth: number;
  const initial = parseIsoParts(normalizeDateInput(raw) || todayIso());
  if (initial) {
    viewYear = initial.y;
    viewMonth = initial.m;
  } else {
    const now = new Date();
    viewYear = now.getFullYear();
    viewMonth = now.getMonth() + 1;
  }

  input.type = "text";
  input.inputMode = "numeric";
  input.placeholder = "YYYY-MM-DD";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.maxLength = 10;
  input.classList.add("sfe-focus-exp-input", "sfe-date-picker-input");

  let root = input.closest<HTMLElement>(".sfe-date-picker");
  let row: HTMLElement;
  let toggle: HTMLButtonElement;
  let popover: HTMLElement;
  let monthLabel: HTMLElement;
  let grid: HTMLElement;

  if (!root) {
    root = document.createElement("div");
    root.className = "sfe-date-picker";
    input.replaceWith(root);

    row = document.createElement("div");
    row.className = "sfe-date-picker-row";

    toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "sfe-date-picker-toggle";
    toggle.setAttribute("aria-label", "Open calendar");
    toggle.innerHTML =
      '<svg viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M5 2a1 1 0 0 0-1 1v1H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1V3a1 1 0 1 0-2 0v1H6V3a1 1 0 0 0-1-1Zm12 7H3v7h14V9Z"/></svg>';

    popover = document.createElement("div");
    popover.className = "sfe-date-picker-popover";
    popover.hidden = true;
    popover.innerHTML = `
      <div class="sfe-date-picker-head">
        <button type="button" class="sfe-date-picker-nav" data-nav="prev" aria-label="Previous month">‹</button>
        <span class="sfe-date-picker-month" data-month-label></span>
        <button type="button" class="sfe-date-picker-nav" data-nav="next" aria-label="Next month">›</button>
      </div>
      <div class="sfe-date-picker-weekdays">${WEEKDAY_LABELS.map((d) => `<span>${d}</span>`).join("")}</div>
      <div class="sfe-date-picker-grid" data-grid></div>`;

    row.append(input, toggle);
    root.append(row, popover);
  } else {
    row = root.querySelector(".sfe-date-picker-row")!;
    toggle = root.querySelector(".sfe-date-picker-toggle") as HTMLButtonElement;
    popover = root.querySelector(".sfe-date-picker-popover") as HTMLElement;
  }

  monthLabel = popover.querySelector("[data-month-label]") as HTMLElement;
  grid = popover.querySelector("[data-grid]") as HTMLElement;

  const notify = (): void => {
    options.onChange?.();
    input.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const setValue = (iso: string): void => {
    input.value = iso;
    notify();
  };

  const renderCalendar = (): void => {
    monthLabel.textContent = `${MONTH_LABELS[viewMonth - 1]} ${viewYear}`;
    grid.innerHTML = "";

    const firstDow = new Date(viewYear, viewMonth - 1, 1).getDay();
    const totalDays = daysInMonth(viewYear, viewMonth);
    const selected = normalizeDateInput(input.value);
    const today = todayIso();

    for (let i = 0; i < firstDow; i += 1) {
      const pad = document.createElement("span");
      pad.className = "sfe-date-picker-day sfe-date-picker-day-pad";
      pad.setAttribute("aria-hidden", "true");
      grid.appendChild(pad);
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const iso = isoFromParts(viewYear, viewMonth, day);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sfe-date-picker-day";
      btn.textContent = String(day);
      btn.dataset.date = iso;
      if (iso === selected) btn.classList.add("sfe-date-picker-day-selected");
      if (iso === today) btn.classList.add("sfe-date-picker-day-today");
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        setValue(iso);
        popover.hidden = true;
        popover.classList.remove("sfe-date-picker-popover-above");
        root?.classList.remove("sfe-date-picker-open");
      });
      grid.appendChild(btn);
    }
  };

  const openPopover = (): void => {
    options.onInteract?.();
    const parts = parseIsoParts(input.value);
    if (parts) {
      viewYear = parts.y;
      viewMonth = parts.m;
    }
    closeAllDatePickers(popover);
    renderCalendar();
    popover.hidden = false;
    root?.classList.add("sfe-date-picker-open");
    if (root) positionDatePickerPopover(root, popover, row);
  };

  const onToggle = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!popover.hidden) {
      popover.hidden = true;
      popover.classList.remove("sfe-date-picker-popover-above");
      root?.classList.remove("sfe-date-picker-open");
      return;
    }
    openPopover();
  };

  const onNav = (e: Event): void => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-nav]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    options.onInteract?.();
    if (btn.dataset.nav === "prev") {
      viewMonth -= 1;
      if (viewMonth < 1) {
        viewMonth = 12;
        viewYear -= 1;
      }
    } else {
      viewMonth += 1;
      if (viewMonth > 12) {
        viewMonth = 1;
        viewYear += 1;
      }
    }
    renderCalendar();
  };

  const onBlur = (): void => {
    const normalized = normalizeDateInput(input.value);
    if (normalized) input.value = normalized;
  };

  const onFocus = (): void => {
    options.onInteract?.();
  };

  toggle.addEventListener("click", onToggle);
  popover.addEventListener("click", onNav);
  input.addEventListener("blur", onBlur);
  input.addEventListener("focus", onFocus);

  input.value = normalizeDateInput(raw);

  return () => {
    toggle.removeEventListener("click", onToggle);
    popover.removeEventListener("click", onNav);
    input.removeEventListener("blur", onBlur);
    input.removeEventListener("focus", onFocus);
    closeAllDatePickers();
  };
}

export function prepareExpDateField(
  input: HTMLInputElement,
  raw?: string | null,
  options: ExpDatePickerOptions = {},
): void {
  mountExpDatePicker(input, raw, options);
}
